import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Database, Download,
  Lock, Loader2, Info, ShieldCheck, Key
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { AdminLayout } from "../../components/AdminLayout";
import { toast } from "sonner";
import adminApi from "../../lib/adminApi";

export default function AdminMaintenancePage() {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const [rsaPublicKey, setRsaPublicKey] = useState("");
  const [rsaKeyLoaded, setRsaKeyLoaded] = useState(false);
  const [savingRsa, setSavingRsa] = useState(false);
  const [hasRsaKey, setHasRsaKey] = useState(false);

  useEffect(() => {
    loadRsaKey();
  }, []);

  const loadRsaKey = async () => {
    try {
      const res = await adminApi.get("/admin/company-config");
      setRsaPublicKey(res.data.admin_rsa_public_key || "");
      setHasRsaKey(res.data.has_admin_rsa_key || false);
      setRsaKeyLoaded(true);
    } catch {
      setRsaKeyLoaded(true);
    }
  };

  const handleSaveRsaKey = async () => {
    setSavingRsa(true);
    try {
      const res = await adminApi.put("/admin/rsa-public-key", { admin_rsa_public_key: rsaPublicKey });
      setHasRsaKey(res.data.has_rsa_key);
      toast.success(rsaPublicKey.trim() ? t("maintenance.rsaPublicKeySaved") : t("maintenance.rsaPublicKeyRemoved"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("maintenance.rsaPublicKeyInvalid"));
    } finally {
      setSavingRsa(false);
    }
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const res = await adminApi.post(
        "/admin/maintenance/backup",
        {},
        { responseType: "blob" }
      );
      const disposition = res.headers["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : `votasegur_backup_${Date.now()}.sql`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("maintenance.downloadSuccess"));
    } catch {
      toast.error(t("maintenance.downloadError"));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 max-w-4xl" data-testid="admin-maintenance-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
              <Database className="h-5 w-5 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white" data-testid="maintenance-title">
                {t("maintenance.title")}
              </h1>
              <p className="text-sm text-slate-400">{t("maintenance.subtitle")}</p>
            </div>
          </div>
        </div>

        {/* Backup Section */}
        <section className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-6" data-testid="backup-section">
          <div className="flex items-center gap-2 mb-4">
            <Download className="h-5 w-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">{t("maintenance.backupSection")}</h2>
          </div>
          <p className="text-sm text-slate-400 mb-6">{t("maintenance.backupDesc")}</p>

          {/* Download button */}
          <Button
            onClick={handleDownload}
            disabled={downloading}
            className="bg-amber-500 hover:bg-amber-600 text-white"
            data-testid="download-backup-btn"
          >
            {downloading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t("maintenance.downloading")}
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                {t("maintenance.download")}
              </>
            )}
          </Button>
        </section>

        {/* RSA Public Key Section */}
        <section className="bg-slate-800 rounded-xl border border-slate-700 p-6 mb-6" data-testid="rsa-key-section">
          <div className="flex items-center gap-2 mb-4">
            <Key className="h-5 w-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">{t("maintenance.rsaPublicKeyTitle")}</h2>
            {hasRsaKey ? (
              <span className="ml-auto text-xs px-2 py-1 rounded bg-emerald-500/20 text-emerald-400 flex items-center gap-1" data-testid="rsa-active-badge">
                <ShieldCheck className="h-3 w-3" /> {t("maintenance.rsaActive")}
              </span>
            ) : (
              <span className="ml-auto text-xs px-2 py-1 rounded bg-slate-600 text-slate-400" data-testid="rsa-inactive-badge">
                {t("maintenance.rsaNotConfigured")}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-400 mb-4">{t("maintenance.rsaPublicKeyDesc")}</p>
          <textarea
            value={rsaPublicKey}
            onChange={(e) => setRsaPublicKey(e.target.value)}
            rows={6}
            className="w-full bg-slate-900 border border-slate-600 rounded-lg p-3 text-sm font-mono text-slate-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none resize-none"
            placeholder={t("maintenance.rsaPublicKeyPlaceholder")}
            data-testid="rsa-public-key-input"
          />
          <div className="flex justify-end mt-3">
            <Button
              onClick={handleSaveRsaKey}
              disabled={savingRsa}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="rsa-save-btn"
            >
              {savingRsa ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <ShieldCheck className="h-4 w-4 mr-2" />}
              {t("common.save")}
            </Button>
          </div>
        </section>

        {/* Restore Instructions */}
        <section className="bg-slate-800 rounded-xl border border-slate-700 p-6" data-testid="restore-instructions-section">
          <div className="flex items-center gap-2 mb-4">
            <Info className="h-5 w-5 text-amber-400" />
            <h2 className="text-lg font-semibold text-white">{t("maintenance.restoreInstructions")}</h2>
          </div>
          <ol className="space-y-3 text-sm text-slate-300 list-decimal list-inside">
            <li className="leading-relaxed">{t("maintenance.restoreStep1")}</li>
            <li className="leading-relaxed">{t("maintenance.restoreStep2")}</li>
            <li className="leading-relaxed">{t("maintenance.restoreStep3")}</li>
          </ol>
        </section>
      </div>
    </AdminLayout>
  );
}
